// oxlint-disable react/jsx-pascal-case
import { type Component, type ComponentProps, type JSX, Show } from "solid-js";
import { cn } from "@tendly/shared";

interface EmptyStateProps {
  /** Lucide icon (compact fallback if no `illustration` is provided). */
  icon?: Component<ComponentProps<"svg">>;
  /** Inline SVG illustration (theme-aware), takes priority over `icon`. */
  illustration?: JSX.Element;
  title: string;
  description?: string;
  /** Optional action (e.g. create button). */
  action?: JSX.Element;
  /**
   * When provided, the illustrative mark (illustration or icon) becomes an
   * interactive target that triggers the same action as the primary CTA (open a
   * dialog, navigate, or focus an input) so the empty state never feels like a
   * dead end. Tapping the mark is a discoverable shortcut to the CTA — leave it
   * undefined for purely informational empty states (the mark stays decorative).
   */
  onIconActivate?: () => void;
  /**
   * Accessible label for the interactive mark (required when `onIconActivate`
   * is set). Should describe the action, e.g. "Add your first asset".
   */
  iconActionLabel?: string;
  class?: string;
}

/** Decorative visual mark (inline illustration or lucide icon). */
function Mark(props: { icon?: Component<ComponentProps<"svg">>; illustration?: JSX.Element }) {
  return (
    <Show
      when={props.illustration}
      fallback={
        <Show when={props.icon}>
          {(icon) => {
            const Icon = icon();
            return <Icon class="h-12 w-12 text-primary/60" />;
          }}
        </Show>
      }
    >
      {(illustration) => <div class="w-44 max-w-full">{illustration()}</div>}
    </Show>
  );
}

export const EmptyState: Component<EmptyStateProps> = (props) => (
  <div class={cn("flex flex-1 flex-col items-center justify-center p-8 text-center", props.class)}>
    <div class="animate-scale-in mb-6">
      {/* When an action is wired, the mark becomes a real button so tap/click/
          keyboard (Enter/Space, native to <button>) triggers the CTA. Otherwise
          it stays a decorative div. The float animation is suppressed under the
          global prefers-reduced-motion guard. */}
      <Show
        when={props.onIconActivate}
        fallback={
          <div class="rounded-full bg-primary/5 p-6 text-primary/70 ring-1 ring-primary/10 animate-float">
            <Mark icon={props.icon} illustration={props.illustration} />
          </div>
        }
      >
        {(activate) => (
          <button
            type="button"
            aria-label={props.iconActionLabel ?? props.title}
            onClick={() => {
              activate()();
            }}
            class="group rounded-full bg-primary/5 p-6 text-primary/70 ring-1 ring-primary/10 animate-float transition-[transform,box-shadow,background-color] duration-200 hover:bg-primary/10 hover:ring-primary/25 hover:shadow-lg hover:shadow-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
          >
            <Mark icon={props.icon} illustration={props.illustration} />
          </button>
        )}
      </Show>
    </div>
    <h3 class="text-lg font-semibold tracking-tight">{props.title}</h3>
    <Show when={props.description}>
      <p class="mt-2 text-sm text-muted-foreground max-w-xs mx-auto">{props.description}</p>
    </Show>
    <Show when={props.action}>
      <div class="mt-6">{props.action}</div>
    </Show>
  </div>
);
